import { v4 as uuidv4 } from "uuid";

const DEVICE_ID_STORAGE_KEY = "flymeta_device_id";
const FINGERPRINT_VERSION = 1;

let cachedDeviceId = null;
let fingerprintPromise = null;

export const getDeviceId = () => {
    if (cachedDeviceId) {
        return cachedDeviceId;
    }

    const storage = getLocalStorage();
    const storedDeviceId = getStoredDeviceId(storage);
    if (storedDeviceId) {
        cachedDeviceId = storedDeviceId;
        return cachedDeviceId;
    }

    cachedDeviceId = uuidv4();
    persistDeviceId(storage, cachedDeviceId);
    return cachedDeviceId;
};

export const getBrowserFingerprint = async () => {
    if (!fingerprintPromise) {
        fingerprintPromise = collectBrowserFingerprint();
    }
    return fingerprintPromise;
};

const collectBrowserFingerprint = async () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        throw new Error("Browser fingerprint requires browser APIs");
    }

    const signals = {
        canvas_hash: await getCanvasHash(),
        webgl: getWebglInfo(),
        screen: getScreenInfo(),
        device_pixel_ratio: window.devicePixelRatio || 1,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        platform: navigator.userAgentData?.platform || navigator.platform || "",
        language: navigator.language || "",
        hardware_concurrency: navigator.hardwareConcurrency || null,
        device_memory: navigator.deviceMemory || null,
        touch_support: Boolean("ontouchstart" in window || navigator.maxTouchPoints > 0),
    };

    return {
        hash: await sha256Hex(stableStringify(signals)),
        version: FINGERPRINT_VERSION,
    };
};

const getCanvasHash = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const context = canvas.getContext("2d");
    if (!context) {
        return "";
    }

    context.textBaseline = "top";
    context.font = "16px Arial";
    context.fillStyle = "#f60";
    context.fillRect(0, 0, 120, 30);
    context.fillStyle = "#069";
    context.fillText("FlyMeta trust profile", 4, 8);
    context.strokeStyle = "rgba(120, 40, 200, 0.7)";
    context.beginPath();
    context.arc(180, 30, 18, 0, Math.PI * 2);
    context.stroke();

    return sha256Hex(canvas.toDataURL());
};

const getWebglInfo = () => {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) {
        return { vendor: "", renderer: "" };
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    return {
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    };
};

const getScreenInfo = () => ({
    width: window.screen?.width || null,
    height: window.screen?.height || null,
    color_depth: window.screen?.colorDepth || null,
    pixel_depth: window.screen?.pixelDepth || null,
});

const stableStringify = (value) => JSON.stringify(sortObject(value));

const sortObject = (value) => {
    if (Array.isArray(value)) {
        return value.map(sortObject);
    }
    if (value && typeof value === "object") {
        return Object.keys(value)
            .sort()
            .reduce((result, key) => ({ ...result, [key]: sortObject(value[key]) }), {});
    }
    return value;
};

const sha256Hex = async (value) => {
    if (!window.crypto?.subtle) {
        throw new Error("Web Crypto SHA-256 is unavailable");
    }
    const data = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
};

const getLocalStorage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
        return null;
    }
};

const getStoredDeviceId = (storage) => {
    try {
        return storage?.getItem(DEVICE_ID_STORAGE_KEY) || "";
    } catch {
        return "";
    }
};

const persistDeviceId = (storage, deviceId) => {
    try {
        storage?.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    } catch {
        // The in-memory device id still gives this page session a stable signal.
    }
};
